/**
 * shareSheetRules — what the exchange share sheet offers, as one pure function
 * (signup E3b-1 step 3). The executable form of
 * docs/blocks/exchange-rules.md: its matrix (first connect, rules
 * 0–6 and 16–18) and the simpler repeat (rule 7, Mark 2026-10-19: `Reconnect`,
 * plus `Share more` only when something is left to share).
 *
 * Item keys are the live-sharing ones (resolveSharedItems / migration 049):
 * contact slots (`phone`, `phone2`, …), `livesIn`, `aboutMe`, and `nameForm`
 * for the full name. The commit path still speaks sharedFields (`currentCity`
 * for Lives in, no name key) — `toSharedFields` converts.
 *
 * Permanent data layer: no React, no network. The caller loads the inputs
 * (shareSheetData) and renders what this returns.
 */
import { getAvailableSharingFields, SHARING_FIELD_DEFINITIONS } from '../../utils/sharingFields';
import { hasAboutMeContent } from '../../utils/profilePayload';
import { hasTwoNames } from '../../utils/contactUtils';
import { resolveSharedItems, FULL_PROFILE_ITEM } from './resolveSharedItems';

export const NAME_ITEM = 'nameForm';
export const LIVES_IN_ITEM = 'livesIn';
export const ABOUT_ME_ITEM = 'aboutMe';

/**
 * The rows `Custom` lists (rule 6; DETAIL §8e order): your full name when you
 * have two names, Lives in, About me, then each contact you hold. Every share
 * kind is measured against this list.
 *
 * @param {Object|null} profile - your own profile
 * @returns {Array<{itemKey: string, field: string|null, type?: string}>}
 *   field = the sharedFields key (null for the name)
 */
export const shareItemsOf = (profile) => {
  if (!profile) return [];
  return [
    ...(hasTwoNames(profile) ? [{ itemKey: NAME_ITEM, field: null }] : []),
    ...(profile.currentCity ? [{ itemKey: LIVES_IN_ITEM, field: 'currentCity' }] : []),
    ...(hasAboutMeContent(profile) ? [{ itemKey: ABOUT_ME_ITEM, field: 'aboutMe' }] : []),
    ...getAvailableSharingFields(profile).map((f) => ({
      itemKey: f.key,
      field: f.key,
      type: f.type,
    })),
  ];
};

/**
 * Ticked item keys → what commitSharedFields takes. The photo always rides;
 * the name is the isFullName switch, not a field.
 *
 * @param {string[]} itemKeys
 * @returns {{sharedFields: string[], isFullName: boolean}}
 */
export const toSharedFields = (itemKeys) => {
  const keys = new Set(itemKeys || []);
  const sharedFields = ['photoUrl'];
  for (const k of keys) {
    if (k === NAME_ITEM || k === FULL_PROFILE_ITEM) continue;
    sharedFields.push(k === LIVES_IN_ITEM ? 'currentCity' : k);
  }
  return { sharedFields, isFullName: keys.has(NAME_ITEM) };
};

const TYPE_LABEL = Object.fromEntries(SHARING_FIELD_DEFINITIONS.map((d) => [d.key, d.label]));

/**
 * A set named by its contents (rule 18; exchange-overlay's `Phone, Instagram +1`):
 * the contacts in it, one name per type, two names then `+N`. Lives in, About
 * me and the name don't name a set. Null when no contact is left.
 *
 * @param {string[]} itemKeys - the set, already narrowed to what you still hold
 * @returns {string|null}
 */
export const contentsLabel = (itemKeys) => {
  const labels = [];
  for (const k of itemKeys || []) {
    const label = TYPE_LABEL[k];
    if (label && !labels.includes(label)) labels.push(label);
  }
  if (labels.length === 0) return null;
  const shown = labels.slice(0, 2).join(', ');
  return labels.length > 2 ? `${shown} +${labels.length - 2}` : shown;
};

/**
 * Groups that can go on the sheet (rule 16: a group shows only once it shares
 * something beyond first name, photo and colour — i.e. something you hold),
 * in sheet order (rule 4): used groups most recent first, then never-used ones
 * in your own group order.
 *
 * @param {Array<{id, name, sortOrder?: number, lastUsedAt?: string|null, items: string[]}>} groups
 * @param {Set<string>} held - item keys you hold (shareItemsOf)
 */
export const shareableGroups = (groups, held) =>
  (groups || [])
    .map((g) => ({ ...g, items: (g.items || []).filter((k) => held.has(k)) }))
    .filter((g) => g.items.length > 0)
    .sort((a, b) => {
      const ta = a.lastUsedAt ? Date.parse(a.lastUsedAt) : null;
      const tb = b.lastUsedAt ? Date.parse(b.lastUsedAt) : null;
      if (ta !== null && tb !== null) return tb - ta;
      if (ta !== null) return -1;
      if (tb !== null) return 1;
      return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    });

/**
 * What this person already receives from you (the live-sharing rule over your
 * own rows: personal picks, the groups you filed them in, Full profile).
 *
 * @param {Object} p
 * @param {Array} p.held - shareItemsOf(profile)
 * @param {Object<string,('on'|'off')>} p.personal - your picks for this person
 * @param {Array<{id, items: string[]}>} p.memberOf - your groups they're in, with what each shares
 * @returns {{fullProfile: boolean, items: Set<string>}}
 */
export const receivedFrom = ({ held, personal = {}, memberOf = [] }) => {
  const fullProfile = personal[FULL_PROFILE_ITEM] === 'on';
  const groupShareSets = Object.fromEntries(memberOf.map((g) => [g.id, { items: g.items || [] }]));
  const { items } = resolveSharedItems({
    holderDetails: held.map((h) => ({ itemKey: h.itemKey })),
    viewerGroupIds: memberOf.map((g) => g.id),
    groupShareSets,
    personalSettings: personal,
  });
  return { fullProfile, items: new Set(items.filter((i) => i.visible).map((i) => i.itemKey)) };
};

/**
 * The sheet.
 *
 * @param {Object} p
 * @param {'first'|'repeat'} p.connect
 * @param {Object|null} p.profile - your own profile
 * @param {Array} [p.groups] - your groups with their share sets (first connect)
 * @param {string[]|null} [p.lastCustom] - your last Custom set (rule 18)
 * @param {{fullProfile: boolean, items: Set<string>}|null} [p.received] - repeat only;
 *   null = not known (yet, or ever): `Reconnect` alone, never a wrong `Share more`
 *   (Mark, 2026-10-20 — moved here from the sheet, E3b-1 refactor row (g))
 * @param {string} [p.peerFirst] - their first name: `Reconnect with <first name>`
 *   (Mark's wording, 2026-10-21)
 * @returns {{atRest: Array<Object>, behindArrow: Array<Object>}}
 *   Each button: { kind: 'full'|'group'|'last_custom'|'custom'|'reconnect'|'share_more',
 *   label, groupId?, items? } — `items` = what the tap shares (item keys).
 */
export const shareSheetButtons = ({
  connect,
  profile,
  groups = [],
  lastCustom = null,
  received,
  peerFirst = null,
}) => {
  const heldList = shareItemsOf(profile);
  const held = new Set(heldList.map((h) => h.itemKey));

  if (connect === 'repeat') {
    // Rule 7: Reconnect alone when you share Full profile with them, or they
    // already receive every row Custom would list; otherwise + Share more.
    const left =
      !!received && !received.fullProfile && heldList.some((h) => !received.items.has(h.itemKey));
    return {
      atRest: [
        { kind: 'reconnect', label: peerFirst ? `Reconnect with ${peerFirst}` : 'Reconnect' },
        ...(left ? [{ kind: 'share_more', label: 'Share more' }] : []),
      ],
      behindArrow: [],
    };
  }

  const full = { kind: 'full', label: 'Full profile', items: [...held] };
  const custom = { kind: 'custom', label: 'Custom' };
  const asButton = (g) => ({ kind: 'group', label: g.name, groupId: g.id, items: g.items });
  const shown = shareableGroups(groups, held);

  if (shown.length === 0) {
    // Rule 18: the last Custom set fills the free slot while a contact in it is held
    const still = (lastCustom || []).filter((k) => held.has(k));
    const label = contentsLabel(still);
    const last = label ? [{ kind: 'last_custom', label, items: still }] : [];
    return { atRest: [full, ...last, custom], behindArrow: [] };
  }
  if (shown.length === 1) return { atRest: [full, asButton(shown[0]), custom], behindArrow: [] };
  return {
    atRest: [full, asButton(shown[0]), asButton(shown[1])],
    behindArrow: [...shown.slice(2).map(asButton), custom],
  };
};

/**
 * What Custom starts with ticked (rule 6 amendment, Mark 2026-10-19): on a
 * first connect your full name and Lives in; opened from a repeat's
 * `Share more`, what this person already receives.
 *
 * @param {'first'|'share_more'} entry
 * @param {Array} held - shareItemsOf(profile)
 * @param {{items: Set<string>}|null} [received]
 * @returns {Set<string>} ticked item keys
 */
export const customStartTicks = (entry, held, received = null) => {
  const keys = new Set((held || []).map((h) => h.itemKey));
  if (entry === 'share_more') {
    return new Set([...(received?.items || [])].filter((k) => keys.has(k)));
  }
  return new Set([NAME_ITEM, LIVES_IN_ITEM].filter((k) => keys.has(k)));
};

/**
 * A Custom-style share as the choice to record (recordShare): what's ticked is
 * 'on', every other row you hold is 'off' — Custom replaces this person's picks,
 * so leaving a row unticked is deliberate (rule 8).
 *
 * @param {Array} held - shareItemsOf(profile)
 * @param {Iterable<string>} ticked - item keys
 * @param {Object} [opts]
 * @param {'custom'|'last_custom'} [opts.kind='custom']
 * @param {boolean} [opts.exchange=true] - false from Detail's sheet
 */
export const customShareKind = (held, ticked, { kind = 'custom', exchange = true } = {}) => {
  const on = new Set(ticked);
  return {
    kind,
    on: (held || []).map((h) => h.itemKey).filter((k) => on.has(k)),
    off: (held || []).map((h) => h.itemKey).filter((k) => !on.has(k)),
    exchange,
  };
};
